/**
 * Página /agenda: header 'Agenda' + Ações, sidebar de filtros e calendário.
 *
 * spec 039: duas abas na URL (`?aba=geral|recurso`, padrão da tela de usuários)
 * — "Agenda" é a de sempre; a de placas usa o mesmo calendário e a mesma
 * navegação, mas só com compromissos, cada um como `HH:MM PLACA` + situação, e
 * com "Agendar <recurso>" como botão principal. Link antigo sem `aba` abre a
 * "Agenda".
 *
 * Adendo da 039: na aba por placa, o switch "Mostrar clientes ocultos"
 * (`?ocultos=1`, padrão desligado, última escolha lembrada no navegador; a URL
 * vence o lembrado). Ligado, vale para as duas pontas — a agenda traz os
 * compromissos de tickets ocultos e a busca do "Agendar" os acha —, senão o
 * agendamento seria criado e sumiria da agenda.
 *
 * Adendo 2 da 039: na aba por placa, ao lado do calendário (acima dele em tela
 * estreita), a lista "Aguardando data": os tickets em etapa de agendamento sem
 * compromisso em aberto, seguindo o switch. O "Agendar" de uma linha abre o
 * drawer com o ticket escolhido e o período do bot — com ele, o drawer abre em
 * "Período" (adendo 3).
 *
 * Adendo 3 da 039: o compromisso de período reagenda sempre pelo drawer do
 * "Agendar", também na aba "Agenda" — o "Editar compromisso" de lá é só
 * horário, e salvar ali apagaria o período (`editsInPlateDrawer`).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { CalendarCheck, CalendarPlus, ChevronDown, ClipboardList, UserRoundMinus } from 'lucide-react';
import type { Absence, Calendar, Commitment, CommitmentPeriod } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { teamListQuery } from '@/features/team/team-queries';
import { cn } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import { agendaEventsOptions, calendarsOptions, errorMessage } from './agenda-queries';
import {
  eventsByDay,
  parseDay,
  toISODay,
  type AgendaEventItem,
  type AgendaFilters,
} from './agenda-format';
import { AgendaCalendar, type AgendaView } from './agenda-calendar';
import { AgendaSidebar, type AgendaTypeKey } from './agenda-sidebar';
import {
  rememberShowHidden,
  rememberedShowHidden,
  showHiddenOf,
  type AgendaSearch,
} from './agenda-search';
import { EditCalendarDialog, NewCalendarDialog } from './calendar-dialogs';
import { CommitmentDrawer } from './commitment-drawer';
import {
  PlateCommitmentDrawer,
  plateTicketOfAwaitingItem,
  type PlateTicketRef,
} from './plate-commitment-drawer';
import { editsInPlateDrawer } from './plate-schedule';
import { AwaitingScheduleList } from './awaiting-schedule-list';
import { AbsenceDialog } from './absence-dialog';
import {
  AbsenceViewDialog,
  AppointmentDialog,
  CommitmentDialog,
  DayEventsDialog,
} from './event-dialogs';

// ── Search params (?aba=recurso&view=month&date=2026-08-14) ─────────────────
// spec 039: num módulo puro, para a guarda da API provar a aba na URL
export { agendaSearchSchema } from './agenda-search';

function visibleRange(view: AgendaView, anchor: Date): { start: Date; end: Date } {
  switch (view) {
    case 'month':
      return { start: startOfWeek(startOfMonth(anchor)), end: endOfWeek(endOfMonth(anchor)) };
    case 'week':
      return { start: startOfWeek(anchor), end: endOfWeek(anchor) };
    case 'day':
      return { start: anchor, end: anchor };
    case 'list':
      return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
  }
}

function toggled<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/** Master: todos marcados → desmarca todos; caso contrário marca todos. */
function toggledAll<T>(set: ReadonlySet<T>, allValues: readonly T[]): Set<T> {
  return set.size === 0 ? new Set(allValues) : new Set<T>();
}

type CalendarDialogState = { mode: 'new' } | { mode: 'edit'; calendar: Calendar };
type CommitmentDialogState = { mode: 'new' } | { mode: 'edit'; commitment: Commitment };
type AbsenceDialogState = { mode: 'new' } | { mode: 'edit'; absence: Absence };
type PlateDialogState =
  | { mode: 'new' }
  | { mode: 'edit'; commitment: Commitment }
  // adendo 2: o "Agendar" da lista "Aguardando data" — o ticket e o período do bot (adendo 3)
  | { mode: 'awaiting'; ticket: PlateTicketRef; botPeriod: CommitmentPeriod | null };

type AgendaTab = AgendaSearch['aba'];

export function AgendaPage() {
  const search = useSearch({ from: '/_authenticated/agenda' });
  const navigate = useNavigate();
  const terms = useTerms();
  const plates = search.aba === 'recurso';
  // adendo: o switch só existe na aba por placa; a aba "Agenda" não muda
  const [rememberedHidden, setRememberedHidden] = useState(() =>
    rememberedShowHidden(() => window.localStorage)
  );
  const showHidden = plates && showHiddenOf(search.ocultos, rememberedHidden);
  const tabs: { value: AgendaTab; label: string }[] = [
    { value: 'geral', label: 'Agenda' },
    { value: 'recurso', label: `Agenda por ${terms.resourceId}` },
  ];

  const view = search.view;
  const anchor = search.date ? parseDay(search.date) : new Date();
  const range = visibleRange(view, anchor);
  const startISO = toISODay(range.start);
  const endISO = toISODay(range.end);
  const days = useMemo(
    () => eachDayOfInterval({ start: range.start, end: range.end }),
    [startISO, endISO] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Dados ─────────────────────────────────────────────────────────────────
  // a aba de placas pede só compromissos: não traz os milhares de apontamentos
  const eventsQ = useQuery(
    agendaEventsOptions(
      plates
        ? { start: startISO, end: endISO, types: ['commitments'], includeHidden: showHidden }
        : { start: startISO, end: endISO }
    )
  );
  const calendarsQ = useQuery(calendarsOptions());
  // a equipe ativa deste tenant (pedido da PoolTrack, 05/10)
  const usersQ = useQuery(teamListQuery());
  const calendars = calendarsQ.data ?? [];
  const activeUsers = useMemo(
    () => [...(usersQ.data?.items ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [usersQ.data]
  );

  // ── Filtros (client-side; sets guardam o que está DESMARCADO) ─────────────
  const [excludedCalendarIds, setExcludedCalendarIds] = useState<Set<number>>(new Set());
  const [excludedUserIds, setExcludedUserIds] = useState<Set<number>>(new Set());
  const [excludedTypes, setExcludedTypes] = useState<Set<AgendaTypeKey>>(new Set());

  // a aba de placas é só compromissos: o filtro de tipo some (spec 039)
  const filters: AgendaFilters = {
    showCommitments: plates || !excludedTypes.has('commitments'),
    showAppointments: !plates && !excludedTypes.has('appointments'),
    showAbsences: !plates && !excludedTypes.has('absences'),
    excludedCalendarIds,
    excludedUserIds,
  };

  const byDay = useMemo(
    () => (eventsQ.data ? eventsByDay(eventsQ.data, filters, startISO, endISO) : new Map<string, AgendaEventItem[]>()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventsQ.data, excludedCalendarIds, excludedUserIds, excludedTypes, startISO, endISO, plates]
  );

  // ── Dialogs ───────────────────────────────────────────────────────────────
  const [calendarDialog, setCalendarDialog] = useState<CalendarDialogState | null>(null);
  const [commitmentDialog, setCommitmentDialog] = useState<CommitmentDialogState | null>(null);
  const [absenceDialog, setAbsenceDialog] = useState<AbsenceDialogState | null>(null);
  const [plateDialog, setPlateDialog] = useState<PlateDialogState | null>(null);
  const [openEvent, setOpenEvent] = useState<AgendaEventItem | null>(null);
  const [moreDay, setMoreDay] = useState<string | null>(null);

  // Compromisso aberto no popup: lê a versão FRESCA do cache (o PATCH done é
  // otimista e o objeto guardado no state ficaria defasado).
  const openCommitment =
    openEvent?.kind === 'commitment'
      ? (eventsQ.data?.commitments.find((c) => c.id === openEvent.commitment.id) ??
        openEvent.commitment)
      : null;

  // ── Navegação ─────────────────────────────────────────────────────────────
  const patchSearch = (patch: Partial<AgendaSearch>) =>
    void navigate({ to: '/agenda', search: { ...search, ...patch } });

  // a escolha vai para a URL (que vence) e fica lembrada para a próxima visita
  const toggleShowHidden = (on: boolean) => {
    rememberShowHidden(() => window.localStorage, on);
    setRememberedHidden(on);
    patchSearch({ ocultos: on ? 1 : 0 });
  };

  const go = (dir: -1 | 1) => {
    const next =
      view === 'week'
        ? addDays(anchor, dir * 7)
        : view === 'day'
          ? addDays(anchor, dir)
          : addMonths(anchor, dir); // mês e lista: ±1 mês
    patchSearch({ date: toISODay(next) });
  };

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Agenda</h1>
        <div className="flex items-center gap-2">
          {plates ? (
            <Button size="sm" onClick={() => setPlateDialog({ mode: 'new' })}>
              <CalendarCheck className="size-4" />
              Agendar {terms.resourceId}
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant={plates ? 'outline' : 'default'}>
                Ações
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onSelect={() => setPlateDialog({ mode: 'new' })}>
                <CalendarCheck />
                Agendar {terms.resourceId}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setCalendarDialog({ mode: 'new' })}>
                <CalendarPlus />
                Nova agenda
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setCommitmentDialog({ mode: 'new' })}>
                <ClipboardList />
                Novo compromisso
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setAbsenceDialog({ mode: 'new' })}>
                <UserRoundMinus />
                Nova ausência
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* spec 039: o tipo de agenda — seletor segmentado, a aba na URL */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div
          role="group"
          aria-label="Tipo de agenda"
          className="flex h-9 w-fit items-center rounded-md border bg-card p-0.5"
        >
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              aria-pressed={search.aba === tab.value}
              onClick={() => patchSearch({ aba: tab.value })}
              className={cn(
                'h-8 cursor-pointer rounded-[5px] px-3 text-sm font-medium transition-colors',
                search.aba === tab.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {plates ? (
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Switch
              checked={showHidden}
              onCheckedChange={toggleShowHidden}
              aria-label="Mostrar clientes ocultos"
            />
            Mostrar clientes ocultos
          </label>
        ) : null}
      </div>

      {/* ── Sidebar + calendário ───────────────────────────────────────── */}
      <div className="mt-4 grid items-start gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        {/* adendo 2: "Aguardando data" na coluna da barra lateral — ao lado do
            calendário no grid largo, acima dele no estreito */}
        <div className="flex min-w-0 flex-col gap-4">
          {plates ? (
            <AwaitingScheduleList
              includeHidden={showHidden}
              onSchedule={(item) =>
                setPlateDialog({
                  mode: 'awaiting',
                  ticket: plateTicketOfAwaitingItem(item),
                  botPeriod: item.botPeriod,
                })
              }
            />
          ) : null}
          <AgendaSidebar
            calendars={calendars}
            calendarsPending={calendarsQ.isPending}
            users={activeUsers}
            usersPending={usersQ.isPending}
            excludedCalendarIds={excludedCalendarIds}
            excludedUserIds={excludedUserIds}
            excludedTypes={excludedTypes}
            onToggleCalendar={(id) => setExcludedCalendarIds((s) => toggled(s, id))}
            onToggleAllCalendars={() =>
              setExcludedCalendarIds((s) =>
                toggledAll(
                  s,
                  calendars.map((c) => c.id)
                )
              )
            }
            onToggleUser={(id) => setExcludedUserIds((s) => toggled(s, id))}
            onToggleAllUsers={() =>
              setExcludedUserIds((s) =>
                toggledAll(
                  s,
                  activeUsers.map((u) => u.id)
                )
              )
            }
            onToggleType={(key) => setExcludedTypes((s) => toggled(s, key))}
            onToggleAllTypes={() =>
              setExcludedTypes((s) => toggledAll(s, ['commitments', 'appointments', 'absences']))
            }
            onNewCalendar={() => setCalendarDialog({ mode: 'new' })}
            onEditCalendar={(calendar) => setCalendarDialog({ mode: 'edit', calendar })}
            showTypes={!plates}
          />
        </div>

        {eventsQ.isError ? (
          <div className="rounded-lg border bg-card p-6 text-sm text-destructive shadow-xs">
            Erro ao carregar a agenda: {errorMessage(eventsQ.error)}
          </div>
        ) : (
          <AgendaCalendar
            view={view}
            anchor={anchor}
            days={days}
            byDay={byDay}
            loading={eventsQ.isPending}
            onPrev={() => go(-1)}
            onNext={() => go(1)}
            onToday={() => patchSearch({ date: toISODay(new Date()) })}
            onViewChange={(next) => patchSearch({ view: next })}
            onEventClick={setOpenEvent}
            onMoreClick={setMoreDay}
            mode={plates ? 'plates' : 'general'}
          />
        )}
      </div>

      {/* ── Dialogs de criação/edição ──────────────────────────────────── */}
      {calendarDialog?.mode === 'new' ? (
        <NewCalendarDialog onClose={() => setCalendarDialog(null)} />
      ) : null}
      {calendarDialog?.mode === 'edit' ? (
        <EditCalendarDialog
          calendar={calendarDialog.calendar}
          onClose={() => setCalendarDialog(null)}
        />
      ) : null}
      {commitmentDialog ? (
        <CommitmentDrawer
          initial={commitmentDialog.mode === 'edit' ? commitmentDialog.commitment : undefined}
          onClose={() => setCommitmentDialog(null)}
        />
      ) : null}
      {plateDialog ? (
        <PlateCommitmentDrawer
          includeHidden={showHidden}
          initial={plateDialog.mode === 'edit' ? plateDialog.commitment : undefined}
          ticket={plateDialog.mode === 'awaiting' ? plateDialog.ticket : undefined}
          botPeriod={plateDialog.mode === 'awaiting' ? plateDialog.botPeriod : undefined}
          onClose={() => setPlateDialog(null)}
        />
      ) : null}
      {absenceDialog ? (
        <AbsenceDialog
          initial={absenceDialog.mode === 'edit' ? absenceDialog.absence : undefined}
          onClose={() => setAbsenceDialog(null)}
        />
      ) : null}

      {/* ── Popups de evento ───────────────────────────────────────────── */}
      {openEvent?.kind === 'appointment' ? (
        <AppointmentDialog
          appointment={openEvent.appointment}
          onClose={() => setOpenEvent(null)}
        />
      ) : null}
      {openCommitment ? (
        <CommitmentDialog
          commitment={openCommitment}
          onClose={() => setOpenEvent(null)}
          onEdit={() => {
            setOpenEvent(null);
            // na aba de placas, reagendar é pelo drawer de placa — se o
            // compromisso couber nele (um dia, uma pessoa); senão, o de sempre.
            // Adendo 3: o de período vai para ele também na "Agenda"
            if (editsInPlateDrawer(openCommitment, plates)) {
              setPlateDialog({ mode: 'edit', commitment: openCommitment });
            } else {
              setCommitmentDialog({ mode: 'edit', commitment: openCommitment });
            }
          }}
        />
      ) : null}
      {openEvent?.kind === 'absence' ? (
        <AbsenceViewDialog
          absence={openEvent.absence}
          onClose={() => setOpenEvent(null)}
          onEdit={() => {
            setOpenEvent(null);
            setAbsenceDialog({ mode: 'edit', absence: openEvent.absence });
          }}
        />
      ) : null}

      {/* ── "mais +N" ──────────────────────────────────────────────────── */}
      {moreDay ? (
        <DayEventsDialog
          dayISO={moreDay}
          events={byDay.get(moreDay) ?? []}
          onSelect={(item) => {
            setMoreDay(null);
            setOpenEvent(item);
          }}
          onClose={() => setMoreDay(null)}
          mode={plates ? 'plates' : 'general'}
        />
      ) : null}
    </div>
  );
}
